import { DateInput } from "@msuite/ui/date-input";
import { FormCheckbox, Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { absenceCategories, getAbsenceCategoryTranslationKey } from "@msuite/utils/absences";
import { guardArray } from "@msuite/utils/array";
import { getColorByString } from "@msuite/utils/colors";
import type { AbsenceFormSchema } from "@msuite/utils/schemas";
import { useFormContext, useWatch } from "react-hook-form";
import { useAbsenceTypes } from "@/hooks/use-absence-types";

export function AbsenceForm() {
	/** Hooks */
	const { t } = useTranslation();

	/** Form */
	const methods = useFormContext<AbsenceFormSchema>();
	const [category, startDate, endDate] = useWatch({
		control: methods.control,
		name: ["category", "startDate", "endDate"],
	});

	/** Query */
	const { absenceTypes, hasAbsenceTypes } = useAbsenceTypes(category);

	/** Render */
	return (
		<div className="v-stack gap-6">
			<Select
				isRequired
				itemNotFoundLabel="Not Found"
				label={t("absences.category.category")}
				control={methods.control}
				isNullable={false}
				name="category"
				options={guardArray(absenceCategories).map((absenceCategory) => ({
					value: absenceCategory,
					label: t(getAbsenceCategoryTranslationKey(absenceCategory)),
					item: { absenceCategory },
				}))}
				renderOption={(item, label) => (
					<div className="h-stack">
						<StatusIndicator color={getColorByString(item.absenceCategory)} />
						<p>{label}</p>
					</div>
				)}
			/>
			{hasAbsenceTypes && (
				<Select
					key={`select-absence-type-${absenceTypes.length}`}
					isRequired
					defaultValue={absenceTypes.at(0)?.id}
					itemNotFoundLabel="Not Found"
					label={t("absences.absence_type")}
					control={methods.control}
					isNullable={false}
					name="absenceTypeId"
					options={absenceTypes.map((absenceType) => ({
						value: absenceType.id,
						label: absenceType.name,
						item: { absenceType },
					}))}
					renderOption={(_, label) => (
						<div className="h-stack">
							<StatusIndicator color={getColorByString(label)} />
							<p>{label}</p>
						</div>
					)}
				/>
			)}
			<div className="v-stack gap-2">
				<div className="grid grid-cols-2 gap-4">
					<DateInput
						isRequired
						label={t("dates.start_date")}
						control={methods.control}
						name="startDate"
						maxDate={endDate}
					/>
					<DateInput
						isRequired
						label={t("dates.end_date")}
						control={methods.control}
						name="endDate"
						minDate={startDate}
					/>
				</div>
				<div className="grid grid-cols-2 gap-4">
					<FormCheckbox
						control={methods.control}
						name="halfDayStart"
					>
						{t("absences.half_day")}
					</FormCheckbox>
					<FormCheckbox
						control={methods.control}
						name="halfDayEnd"
					>
						{t("absences.half_day")}
					</FormCheckbox>
				</div>
			</div>
		</div>
	);
}
